import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Play, 
  RotateCcw, 
  CheckCircle, 
  SkipForward,
  Users,
  Clock,
  Volume2,
  Loader2,
  RefreshCw,
  ChevronRight,
  ExternalLink,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodPantryTabletQueuePage = () => {
  const [activeQueue, setActiveQueue] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [nowServing, setNowServing] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [queueLogs, setQueueLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('fp-nashville');
  const [operationalMode, setOperationalMode] = useState('nashville');
  const [locationInfo, setLocationInfo] = useState(null);
  
  // Jump to dialog
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [selectedForJump, setSelectedForJump] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/food-pantry/tablet-queue/${activeTab}`);
      setActiveQueue(response.data.active_queue || []);
      setCompleted(response.data.completed || []);
      setNowServing(response.data.now_serving || null);
      setStatistics(response.data.statistics || {});
      setQueueLogs(response.data.queue_logs || []);
      // Set operational mode from API response
      setOperationalMode(response.data.operational_mode || 'nashville');
      setLocationInfo(response.data.location || null);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
      toast.error('Failed to load queue data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Initial fetch and auto-refresh every 5 seconds
  useEffect(() => {
    setLoading(true);
    fetchQueue();
    const interval = setInterval(fetchQueue, 5000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const performAction = async (action, checkinId = null, queueNumber = null) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/food-pantry/queue-action`, {
        location_id: activeTab,
        action: action,
        checkin_id: checkinId,
        queue_number: queueNumber
      });
      
      if (response.data.success) {
        const actionLabels = {
          call_next: 'Called next participant',
          call_again: 'Replayed announcement',
          jump_to: 'Jumped to participant',
          complete: 'Marked as completed'
        };
        toast.success(actionLabels[action] + (response.data.queue_number ? ` #${response.data.queue_number}` : ''));
        fetchQueue();
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
      setShowJumpDialog(false);
      setSelectedForJump(null);
    }
  };

  const handleCallNext = () => performAction('call_next');
  const handleCallAgain = () => performAction('call_again');
  const handleComplete = () => performAction('complete');
  
  // Charlotte-specific: Complete a specific participant directly
  const handleCompleteParticipant = (checkin) => {
    performAction('complete', checkin.id, checkin.queue_number);
  };
  
  const handleJumpTo = (checkin) => {
    setSelectedForJump(checkin);
    setShowJumpDialog(true);
  };
  
  const confirmJumpTo = () => {
    if (selectedForJump) {
      performAction('jump_to', selectedForJump.id, selectedForJump.queue_number);
    }
  };

  // Check if location is in Charlotte Mode based on operational_mode from API
  const isCharlotteMode = operationalMode === 'charlotte';

  const getStatusBadge = (status) => {
    if (status === 'Now Serving') return <Badge className="bg-green-500">Now Serving</Badge>;
    if (status === 'Skipped') return <Badge className="bg-orange-500">Skipped</Badge>;
    if (status === 'Called') return <Badge className="bg-blue-500">Called</Badge>;
    if (status?.includes('Waiting') || status?.includes('Eligible')) return <Badge className="bg-blue-500">Waiting</Badge>;
    if (status?.includes('New Client')) return <Badge className="bg-purple-500">New Client</Badge>;
    return <Badge variant="secondary">{status || 'Waiting'}</Badge>;
  };

  const formatName = (firstName, lastName) => {
    const initial = firstName ? firstName.charAt(0).toUpperCase() : '?';
    const last = lastName || 'Unknown';
    return `${initial}. ${last}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    try {
      // For Charlotte Mode, convert to Eastern Time
      if (operationalMode === 'charlotte') {
        const today = new Date().toISOString().split('T')[0];
        const fullDateTime = timeStr.includes('T') ? timeStr : `${today}T${timeStr}Z`;
        const date = new Date(fullDateTime);
        return date.toLocaleTimeString('en-US', { 
          timeZone: 'America/New_York',
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true
        });
      }
      // For Nashville Mode, keep existing behavior
      if (timeStr.includes('T')) {
        const date = new Date(timeStr);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      }
      return timeStr.substring(0, 5);
    } catch {
      return timeStr?.substring(0, 5) || '--:--';
    }
  };

  const openLobbyDisplay = () => {
    const lobbyUrl = activeTab === 'fp-nashville' ? '/lobby-display' : '/lobby-display-charlotte';
    window.open(lobbyUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6" data-testid="tablet-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Queue Management</h1>
          <p className="text-gray-600">Food Pantry Staff Console</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchQueue} disabled={actionLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={openLobbyDisplay}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Lobby Display
          </Button>
        </div>
      </div>

      {/* Location Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="fp-nashville" className="text-base">Nashville</TabsTrigger>
          <TabsTrigger value="fp-charlotte" className="text-base">Charlotte</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Now Serving Display - Nashville Mode only */}
      {!isCharlotteMode && (
        <Card className={`mb-6 ${nowServing ? 'bg-green-50 border-green-300' : 'bg-gray-50'}`}>
          <CardContent className="p-6 md:p-8">
            <div className="text-center">
              <p className="text-lg text-gray-600 mb-2">NOW SERVING</p>
              {nowServing ? (
                <>
                  <p className="text-6xl md:text-8xl font-bold text-green-600 mb-2">
                    #{nowServing.queue_number}
                  </p>
                  <p className="text-xl text-gray-700">{nowServing.participant_name}</p>
                </>
              ) : (
                <p className="text-4xl font-bold text-gray-400">---</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons - Nashville Mode only */}
      {!isCharlotteMode && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Button 
            size="lg" 
            className="h-20 md:h-24 text-lg md:text-xl bg-[hsl(302,58%,35%)] hover:bg-[hsl(302,58%,45%)] text-white"
            onClick={handleCallNext}
            disabled={actionLoading || activeQueue.filter(c => c.queue_status !== 'Now Serving').length === 0}
            data-testid="call-next-btn"
          >
            <Play className="h-6 w-6 md:h-8 md:w-8 mr-2" />
            Next (Call)
          </Button>
          
          <Button 
            size="lg" 
            className="h-20 md:h-24 text-lg md:text-xl bg-[hsl(302,58%,45%)] hover:bg-[hsl(302,58%,55%)] text-white"
            onClick={handleCallAgain}
            disabled={actionLoading || !nowServing}
            data-testid="call-again-btn"
          >
            <RotateCcw className="h-6 w-6 md:h-8 md:w-8 mr-2" />
            Again (Call)
          </Button>
          
          <Button 
            size="lg" 
            className="h-20 md:h-24 text-lg md:text-xl bg-green-600 hover:bg-green-700 text-white"
            onClick={handleComplete}
            disabled={actionLoading || !nowServing}
            data-testid="complete-btn"
          >
            <CheckCircle className="h-6 w-6 md:h-8 md:w-8 mr-2" />
            Complete
          </Button>
          
          <Button 
            size="lg" 
            className="h-20 md:h-24 text-lg md:text-xl bg-[hsl(302,58%,25%)] hover:bg-[hsl(302,58%,35%)] text-white border-2 border-[hsl(302,58%,45%)]"
            onClick={() => setShowJumpDialog(true)}
            disabled={actionLoading || activeQueue.length === 0}
            data-testid="jump-to-btn"
          >
            <SkipForward className="h-6 w-6 md:h-8 md:w-8 mr-2" />
            Jump To
          </Button>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 mx-auto mb-1 text-blue-600" />
            <p className="text-2xl font-bold">{statistics.total_today || 0}</p>
            <p className="text-xs text-gray-600">Total Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 mx-auto mb-1 text-yellow-600" />
            <p className="text-2xl font-bold">{statistics.waiting || 0}</p>
            <p className="text-xs text-gray-600">Waiting</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Volume2 className="h-6 w-6 mx-auto mb-1 text-green-600" />
            <p className="text-2xl font-bold">{statistics.in_progress || 0}</p>
            <p className="text-xs text-gray-600">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 mx-auto mb-1 text-gray-600" />
            <p className="text-2xl font-bold">{statistics.completed || 0}</p>
            <p className="text-xs text-gray-600">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Current Queue List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Current Queue
            {isCharlotteMode && <Badge variant="secondary" className="ml-2">Charlotte Mode - Mark Complete Only</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px] md:h-[400px]">
            {activeQueue.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No participants in queue</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeQueue.map((checkin) => {
                  const isNowServing = nowServing && nowServing.checkin_id === checkin.id;
                  
                  // Charlotte Mode simplified view - no click to jump, only Complete button
                  if (isCharlotteMode) {
                    return (
                      <div
                        key={checkin.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-white dark:bg-card"
                        data-testid={`queue-item-${checkin.queue_number}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold text-primary">
                            #{checkin.queue_number}
                          </span>
                          <div>
                            <p className="font-medium">{formatName(checkin.first_name, checkin.last_name)}</p>
                            <p className="text-sm text-gray-500">
                              Checked in: {formatTime(checkin.checkin_time)} ET
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(checkin.queue_status)}
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white ml-2"
                            onClick={() => handleCompleteParticipant(checkin)}
                            disabled={actionLoading}
                            data-testid={`complete-btn-${checkin.queue_number}`}
                          >
                            {actionLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  
                  // Nashville view - clickable rows with jump functionality
                  return (
                    <div
                      key={checkin.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        isNowServing 
                          ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700' 
                          : 'bg-white dark:bg-card hover:bg-muted/50'
                      }`}
                      onClick={() => !isNowServing && handleJumpTo(checkin)}
                      data-testid={`queue-item-${checkin.queue_number}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`text-2xl font-bold ${isNowServing ? 'text-green-600' : 'text-primary'}`}>
                          #{checkin.queue_number}
                        </span>
                        <div>
                          <p className="font-medium">{formatName(checkin.first_name, checkin.last_name)}</p>
                          <p className="text-sm text-gray-500">
                            Checked in: {formatTime(checkin.checkin_time)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(isNowServing ? 'Now Serving' : checkin.queue_status)}
                        {!isNowServing && <ChevronRight className="h-5 w-5 text-gray-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Recent Activity Log */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[150px]">
            {queueLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No activity yet today</p>
            ) : (
              <div className="space-y-2">
                {queueLogs.slice(0, 10).map((log) => (
                  <div key={log.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        #{log.queue_number}
                      </Badge>
                      <span className="capitalize">{log.action.replace('_', ' ')}</span>
                    </div>
                    <div className="text-gray-500 text-xs">
                      {log.staff_name} • {formatTime(log.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Jump To Dialog - Nashville Mode only */}
      {!isCharlotteMode && (
        <Dialog open={showJumpDialog} onOpenChange={setShowJumpDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Jump To Participant</DialogTitle>
              <DialogDescription>
                Select a participant to call next. The current participant will be marked as skipped.
              </DialogDescription>
            </DialogHeader>
            
            {selectedForJump ? (
              <div className="py-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-4xl font-bold text-blue-600 mb-2">#{selectedForJump.queue_number}</p>
                  <p className="text-lg">{formatName(selectedForJump.first_name, selectedForJump.last_name)}</p>
                </div>
                <p className="text-sm text-gray-600 mt-4 text-center">
                  This will call participant #{selectedForJump.queue_number} and play the announcement.
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[300px] py-4">
                <div className="space-y-2">
                  {activeQueue.filter(c => !nowServing || c.id !== nowServing.checkin_id).map((checkin) => (
                    <div
                      key={checkin.id}
                      className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedForJump(checkin)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold text-primary">#{checkin.queue_number}</span>
                        <span>{formatName(checkin.first_name, checkin.last_name)}</span>
                      </div>
                      {getStatusBadge(checkin.queue_status)}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowJumpDialog(false); setSelectedForJump(null); }}>
                Cancel
              </Button>
              {selectedForJump && (
                <Button onClick={confirmJumpTo} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <SkipForward className="h-4 w-4 mr-2" />}
                  Call #{selectedForJump.queue_number}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FoodPantryTabletQueuePage;
